import React, { useMemo, useState } from 'react';
import { Briefcase, Calculator, Calendar, Clock } from 'lucide-react';
import Card from '../../components/UI/Card';
import CalculatorInput from '../../components/Tools/CalculatorInput';
import useTranslation from '../../hooks/useTranslation';

const FREQUENCY_CONFIG = [
  { value: 'hourly', prop: 'hourly', decimals: 2 },
  { value: 'daily', prop: 'daily' },
  { value: 'weekly', prop: 'weekly' },
  { value: 'biweekly', prop: 'biweekly' },
  { value: 'semimonthly', prop: 'semiMonthly' },
  { value: 'monthly', prop: 'monthly' },
  { value: 'quarterly', prop: 'quarterly' },
  { value: 'annual', prop: 'annual' },
];

const FREQUENCY_LABEL_KEYS = {
  hourly: 'salary.range.hourly',
  daily: 'salary.range.daily',
  weekly: 'salary.range.weekly',
  biweekly: 'salary.range.biweekly',
  semimonthly: 'salary.range.semiMonthly',
  monthly: 'salary.range.monthly',
  quarterly: 'salary.range.quarterly',
  annual: 'salary.range.annual',
};

const initialInputs = {
  amount: '50',
  frequency: 'hourly',
  hoursPerWeek: '40',
  daysPerWeek: '5',
  holidaysPerYear: '10',
  vacationDaysPerYear: '15',
};

const WORK_WEEKS_PER_YEAR = 52;
const WEEKDAYS_PER_YEAR = 260; // 52 weeks * 5 weekdays

const formatCurrency = (value, decimals = 0) => {
  const numericValue = Number(value);
  const safeValue = Number.isFinite(numericValue) ? numericValue : 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(safeValue);
};

const normalizeToHour = (amount, frequency, hoursPerWeek) => {
  const hoursYear = hoursPerWeek * WORK_WEEKS_PER_YEAR;
  switch (frequency) {
    case 'hourly':
      return amount;
    case 'daily':
      return amount / hoursPerWeek;
    case 'weekly':
      return amount / hoursPerWeek;
    case 'biweekly':
      return amount / (hoursPerWeek * 2);
    case 'semimonthly':
      return (amount * 24) / hoursYear;
    case 'monthly':
      return (amount * 12) / hoursYear;
    case 'quarterly':
      return (amount * 4) / hoursYear;
    case 'annual':
      return amount / hoursYear;
    default:
      return amount;
  }
};

const buildSalaryTable = (inputs) => {
  const amount = Math.max(parseFloat(inputs.amount) || 0, 0);
  const hoursPerWeek = Math.max(parseFloat(inputs.hoursPerWeek) || 0, 0);
  const daysPerWeek = Math.max(parseFloat(inputs.daysPerWeek) || 0, 0);
  const holidaysPerYear = Math.max(parseFloat(inputs.holidaysPerYear) || 0, 0);
  const vacationDaysPerYear = Math.max(parseFloat(inputs.vacationDaysPerYear) || 0, 0);

  if (amount === 0 || hoursPerWeek === 0 || daysPerWeek === 0) {
    return { rows: [], inputHourly: 0 };
  }

  const hoursPerDay = hoursPerWeek / daysPerWeek;
  const workingDays = Math.max(WEEKDAYS_PER_YEAR - holidaysPerYear - vacationDaysPerYear, 1);
  const workingWeeks = workingDays / daysPerWeek;
  const workingHoursPerYear = workingDays * hoursPerDay;

  const adjustedHoursPerYear = hoursPerWeek * WORK_WEEKS_PER_YEAR;

  let unadjustedHourly = 0;
  switch (inputs.frequency) {
    case 'hourly':
      unadjustedHourly = amount;
      break;
    case 'daily':
      unadjustedHourly = hoursPerDay > 0 ? amount / hoursPerDay : 0;
      break;
    case 'weekly':
      unadjustedHourly = hoursPerWeek > 0 ? amount / hoursPerWeek : 0;
      break;
    case 'biweekly':
      unadjustedHourly = (hoursPerWeek > 0) ? amount / (hoursPerWeek * 2) : 0;
      break;
    case 'semimonthly':
      unadjustedHourly = workingHoursPerYear > 0 ? (amount * 24) / workingHoursPerYear : 0;
      break;
    case 'monthly':
      unadjustedHourly = workingHoursPerYear > 0 ? (amount * 12) / workingHoursPerYear : 0;
      break;
    case 'quarterly':
      unadjustedHourly = workingHoursPerYear > 0 ? (amount * 4) / workingHoursPerYear : 0;
      break;
    case 'annual':
    default:
      unadjustedHourly = workingHoursPerYear > 0 ? amount / workingHoursPerYear : 0;
      break;
  }

  const adjustedHourly = adjustedHoursPerYear > 0
    ? unadjustedHourly * (workingHoursPerYear / adjustedHoursPerYear)
    : 0;

  const buildPeriodValues = (hourly) => ({
    hourly,
    daily: hourly * hoursPerDay,
    weekly: hourly * hoursPerWeek,
    biweekly: hourly * hoursPerWeek * 2,
    semiMonthly: hourly * (adjustedHoursPerYear / 24),
    monthly: hourly * (adjustedHoursPerYear / 12),
    quarterly: hourly * (adjustedHoursPerYear / 4),
    annual: hourly * adjustedHoursPerYear,
  });

  const adjusted = buildPeriodValues(adjustedHourly);
  const unadjusted = buildPeriodValues(unadjustedHourly);

  // Annual values
  adjusted.annual = adjustedHourly * adjustedHoursPerYear;
  unadjusted.annual = unadjustedHourly * adjustedHoursPerYear;

  const rows = FREQUENCY_CONFIG.map(({ value, prop, decimals }) => ({
    key: value,
    decimals: decimals ?? 0,
    unadjusted: unadjusted[prop],
    adjusted: adjusted[prop],
  }));

  return { rows, inputHourly: unadjustedHourly, adjustedHourly };
};

const SalaryCalculator = () => {
  const [formValues, setFormValues] = useState(initialInputs);
  const [inputs, setInputs] = useState(initialInputs);
  const results = useMemo(() => buildSalaryTable(inputs), [inputs]);
  const { t, language } = useTranslation();

  const frequencyOptions = useMemo(
    () => FREQUENCY_CONFIG.map(({ value }) => ({
      value,
      label: t(FREQUENCY_LABEL_KEYS[value], value),
    })),
    [language, t]
  );

  const handleInputChange = (field, value) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCalculate = () => {
    setInputs(formValues);
  };

  const handleClear = () => {
    setFormValues(initialInputs);
    setInputs(initialInputs);
  };

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Briefcase className="h-5 w-5 text-primary-600" />
          {t('salary.title', 'Salary Calculator')}
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          {t('salary.description', 'Convert pay between hourly, daily, weekly, and annual amounts.')}
        </p>
      </Card>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-6 space-y-6">
          <div className="flex items-center gap-2">
            <Calculator className="w-5 h-5 text-primary-600" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('salary.inputs.title')}</h3>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('salary.amount.label')}</span>
              <div className="grid grid-cols-5 gap-3 items-end">
                <div className="col-span-3 space-y-2">
                  <label className="block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {t('salary.amount.field')}
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-500 dark:text-gray-400">
                      $
                    </span>
                    <input
                      type="number"
                      value={formValues.amount}
                      onChange={(e) => handleInputChange('amount', e.target.value)}
                      className="block w-full pl-7 pr-3 py-2 border border-gray-300 dark:border-dark-600 rounded-lg bg-white dark:bg-dark-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 input-focus"
                    />
                  </div>
                </div>
                <div className="col-span-2 space-y-2">
                  <label className="block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {t('salary.amount.per')}
                  </label>
                  <select
                    value={formValues.frequency}
                    onChange={(e) => handleInputChange('frequency', e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 dark:border-dark-600 rounded-lg bg-white dark:bg-dark-700 text-gray-900 dark:text-white input-focus"
                  >
                    {frequencyOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <CalculatorInput
              label={t('salary.hoursPerWeek')}
              value={formValues.hoursPerWeek}
              onChange={(value) => handleInputChange('hoursPerWeek', value)}
              type="number"
            />
            <CalculatorInput
              label={t('salary.daysPerWeek')}
              value={formValues.daysPerWeek}
              onChange={(value) => handleInputChange('daysPerWeek', value)}
              type="number"
            />
            <CalculatorInput
              label={t('salary.holidaysPerYear')}
              value={formValues.holidaysPerYear}
              onChange={(value) => handleInputChange('holidaysPerYear', value)}
              type="number"
            />
            <CalculatorInput
              label={t('salary.vacationPerYear')}
              value={formValues.vacationDaysPerYear}
              onChange={(value) => handleInputChange('vacationDaysPerYear', value)}
              type="number"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={handleCalculate}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-primary-600 text-white py-2 font-semibold shadow-sm hover:bg-primary-700 transition-colors"
            >
              <span>{t('tool.common.calculate')}</span>
            </button>
            <button
              type="button"
              onClick={handleClear}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 dark:border-dark-600 py-2 text-gray-700 dark:text-gray-200 font-semibold hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors"
            >
              {t('tool.common.clear')}
            </button>
          </div>

          <Card className="p-4 bg-gray-50 dark:bg-dark-800 border border-gray-200 dark:border-dark-700">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-primary-600" />
              <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-100">{t('tool.common.assumptions')}</h4>
            </div>
            <ul className="list-disc list-inside text-xs text-gray-600 dark:text-gray-300 space-y-1">
              <li>{t('salary.assumption1')}</li>
              <li>{t('salary.assumption2')}</li>
              <li>{t('salary.assumption3')}</li>
            </ul>
          </Card>
        </Card>

        <Card className="p-6 border border-gray-200 dark:border-dark-700">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('salary.result.heading')}</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-700">
              <thead className="bg-gray-50 dark:bg-dark-800">
                <tr className="text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wide">
                  <th className="px-4 py-3 text-left">{t('salary.result.frequency')}</th>
                  <th className="px-4 py-3 text-left">{t('salary.result.unadjusted')}</th>
                  <th className="px-4 py-3 text-left">{t('salary.result.adjusted')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-dark-700">
                {results.rows.map((row, index) => {
                  const decimals = row.decimals ?? (row.key === 'hourly' ? 2 : 0);
                  const label = t(FREQUENCY_LABEL_KEYS[row.key], row.key);
                  return (
                    <tr key={row.key} className={index % 2 === 0 ? 'bg-white dark:bg-dark-900' : 'bg-gray-50 dark:bg-dark-800'}>
                      <td className="px-4 py-3 text-sm font-medium text-gray-800 dark:text-gray-100">{label}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white font-semibold">{formatCurrency(row.unadjusted, decimals)}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{formatCurrency(row.adjusted, decimals)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default SalaryCalculator;
